import React, { useEffect } from "react";

import { Box, Button, Typography } from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import { makeStyles } from "tss-react/mui";

import { AddIconButton } from "@/components/IconButtons";
import {
  BodyMeasurementListItemFragment,
  BodyMeasurementListItem_SettingFragment,
  Gender,
  Maybe,
} from "@/gql/graphql";
import { kilogramsToPounds, round, truncate } from "@/utils/number";

import AccordionSummaryItem from "./AccordionSummaryItem";
import BodyMeasurementListItem from "./BodyMeasurementListItem";
import { getDevineIdealBodyWeight } from "../utils/getIdealBodyWeight";

const useStyles = makeStyles()((theme) => ({
  root: {
    marginTop: theme.spacing(4),
    width: "100%",
    backgroundColor: theme.palette.common.white,
    padding: theme.spacing(2, 4),
  },
  heading: {
    fontSize: theme.typography.pxToRem(15),
    flexBasis: "33.33%",
    flexShrink: 0,
  },
  summaryHeader: {
    marginRight: 20,
    display: "flex",
    flexDirection: "row",
  },
  secondaryHeading: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text.secondary,
  },
}));

const useButtonStyles = makeStyles()({
  root: {
    height: "2rem",
    marginRight: 8,
  },
  disabled: {
    border: 0,
  },
});

interface Props {
  bodyMeasurements?: BodyMeasurementListItemFragment | null;
  settings?: BodyMeasurementListItem_SettingFragment | null;
  targetWeight: Maybe<number> | undefined;
  toggleEdit: () => void;
  toggleAdd: () => void;
  toggleDelete: () => void;
  gender: Gender | undefined;
  latestHeightCm: number | undefined;
  latestWeightKg: number | undefined;
}

const BodyMeasurementList: React.FC<Props> = ({
  bodyMeasurements,
  targetWeight,
  toggleEdit,
  toggleAdd,
  toggleDelete,
  settings,
  gender,
  latestHeightCm,
  latestWeightKg,
}) => {
  const { classes } = useStyles();
  const buttonClasses = useButtonStyles();
  const location = useLocation();
  const historyLink = "body-measurement-history";

  useEffect(() => {
    if (location.hash === "#body-measurement") {
      const element = document.getElementById("body-measurement");
      element?.scrollIntoView();
    }
  }, [location]);

  const idealBodyWeight = round(
    getDevineIdealBodyWeight(latestHeightCm, gender) ?? 0,
    2
  );

  return (
    <div className={classes.root} id="body-measurement">
      <Box
        width="100%"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flex={1}
        >
          <Typography variant="h6" fontWeight="bold">
            Body measurements
          </Typography>
          <div className={classes.summaryHeader}>
            <AccordionSummaryItem
              title="Current Height"
              text={`${bodyMeasurements?.heightCm ?? 0} cm`}
              icon="height"
            />
            <Box ml={3} />
            <AccordionSummaryItem
              title="Goal Weight"
              text={`${truncate(kilogramsToPounds(targetWeight ?? 0), 1)} lbs`}
              secondaryText={`(${targetWeight} kg)`}
              icon="goal-weight"
            />
            <Box ml={3} />
            <AccordionSummaryItem
              title="Ideal Weight"
              text={`${idealBodyWeight} kg`}
              secondaryText={`(${round(
                kilogramsToPounds(idealBodyWeight),
                2
              )} lbs)`}
              icon="ideal-weight"
            />
          </div>
        </Box>
        <Box display="flex">
          <AddIconButton variant="contained" onClick={toggleAdd} />
          <Box marginRight={2} />
          <Button
            variant="outlined"
            classes={buttonClasses}
            component={Link}
            to={historyLink}
          >
            View History
          </Button>
        </Box>
      </Box>
      {bodyMeasurements && settings ? (
        <BodyMeasurementListItem
          onEdit={toggleEdit}
          onDelete={toggleDelete}
          targetWeight={targetWeight ?? 0}
          bodyMeasurements={bodyMeasurements}
          settings={settings}
          gender={gender}
          latestHeightCm={latestHeightCm}
          latestWeightKg={latestWeightKg}
        />
      ) : (
        <Typography>No data</Typography>
      )}
    </div>
  );
};

export default BodyMeasurementList;
